import {
  UploadWorkflowFilesRequest,
  WorkflowCreateDirRequest,
  WorkflowsService,
} from '@/api';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useStore } from '@nanostores/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  FileText,
  FileUp,
  FolderIcon,
  Loader2,
  X,
} from 'lucide-react';
import { toast } from 'sonner';

interface Props {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setNodes: any;
  selectedNode: any;
  workFlowId: string;
}
export default function WorkflowStorage({
  open,
  setOpen,
  setNodes,
  selectedNode,
  workFlowId,
}: Props) {
  const org = useStore($org);
  const page = useStore($router);
  const queryClient = useQueryClient();

  console.log('page', page);
  const [basePath, setBasePath] = useState(
    `${org?.id}/workflows/${workFlowId}/`,
  );
  const [searchPath, setSearchPath] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fileToBeUploaded, setFileToBeUploaded] = useState<File | null>();

  function traverseBack(path: string): void {
    // Split the string by '/' and remove the last segment
    let currentString = path;
    const parts = currentString.split('/');
    parts.pop(); // Remove the last segment
    parts.pop();
    currentString = parts.join('/'); // Join the remaining parts
    if (currentString !== '') {
      currentString = currentString + '/'; // If the string is empty, set it to '/'
    }
    setSearchPath(currentString); // Update the search path
  }

  // mutation to create a dir
  const createDir = useMutation({
    mutationFn: async (payload: WorkflowCreateDirRequest) => {
      // call the api to create a new workspace
      if (workFlowId) {
        const res = await WorkflowsService.createDir({
          orgId: org?.id || '',
          workflowId: workFlowId,
          dirName: payload.dirName,
          fullPath: `${basePath}${searchPath}`,
        });
        return res;
      }
    },
    onSuccess: async data => {
      await queryClient.invalidateQueries({
        queryKey: [
          'lisWorkflowFilesAndDirs',
          org?.id,
          workFlowId,
          basePath,
          searchPath,
        ],
      });
    },
    onError: error => {
      console.error(error);
      // show a toast message
      toast('An error occurred :: Unable to create dir.', {
        description: error.message,
        duration: 5000,
      });
    },
  });

  // mutation to upload a file
  const uploadFile = useMutation({
    mutationFn: async (payload: UploadWorkflowFilesRequest) => {
      // call the api to create a new workspace
      if (workFlowId) {
        const res = await WorkflowsService.uploadWorkflowFiles({
          orgId: org?.id || '',
          workflowId: workFlowId,
          fullPath: `${basePath}${searchPath}`,
          fileName: payload.fileName,
          fileContentsBase64: payload.fileContentsBase64,
        });
        return res;
      }
    },
    onSuccess: async data => {
      await queryClient.invalidateQueries({
        queryKey: [
          'lisWorkflowFilesAndDirs',
          org?.id,
          workFlowId,
          basePath,
          searchPath,
        ],
      });
    },
    onError: error => {
      console.error(error);
      // show a toast message
      toast('An error occurred :: Unable to create dir.', {
        description: error.message,
        duration: 5000,
      });
    },
  });

  // Function to handle button click
  const handleButtonClick = () => {
    fileInputRef && fileInputRef.current && fileInputRef.current.click(); // Trigger file input click
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      setFileToBeUploaded(selectedFile); // Set the selected
      const fileBase64 = await file2Base64(selectedFile);
      await uploadFile.mutateAsync({
        orgId: org?.id || '',
        workflowId: workFlowId,
        fullPath: `${basePath}${searchPath}`,
        fileName: selectedFile.name,
        fileContentsBase64: fileBase64,
      });
    }
  };

  // query to get the collaborators of the workspace
  const {
    data: workflowFilesAndDirs,
    error,
    isLoading,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: [
      'lisWorkflowFilesAndDirs',
      org?.id,
      workFlowId,
      basePath,
      searchPath,
    ],
    enabled: !!(org?.id && workFlowId),
    queryFn: async () => {
      const res = await WorkflowsService.listWorkflowFilesAndDirectories(
        org?.id || '',
        workFlowId || '',
        `${basePath}${searchPath}`,
      );
      return res;
    },
  });

  return (
    <div
      className={`absolute right-6 top-20 min-h-[160px] min-w-[360px] rounded-lg bg-white p-4 pl-6 pr-6 ${!open && 'hidden'} z-10`}
    >
      <div className="flex justify-between">
        <div className="flex items-center">
          <ArrowLeft
            className="mr-2 h-4 w-4 cursor-pointer"
            onClick={() => traverseBack(searchPath)}
          />
          <p className="text-sm font-semibold tracking-tight">
            Workflow file manager
          </p>
        </div>

        <button
          type="button"
          onClick={() => {
            setOpen(false);
            setSearchPath('');
          }}
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {isLoading && (
        <div className="flex h-[100px] w-full items-center justify-center">
          <Loader2 className="h-4 w-4 animate-spin" />
        </div>
      )}

      {!isLoading &&
        workflowFilesAndDirs?.directories?.length === 0 &&
        workflowFilesAndDirs?.files?.length === 0 && (
          <div className="mt-2 flex h-[100px] w-[100%] flex-col items-center justify-center text-xs text-muted-foreground">
            You don't have any files or directories in this workflow.
          </div>
        )}

      {!isLoading && workflowFilesAndDirs && (
        <div className="mt-2 flex flex-col">
          {workflowFilesAndDirs?.directories?.map((dir: any) => (
            <div
              className="mt-1 flex cursor-pointer items-center text-sm"
              onClick={() => {
                setSearchPath(`${searchPath}${dir}`);
              }}
            >
              <FolderIcon className="mr-2 h-4 w-4" />
              {dir}
            </div>
          ))}
        </div>
      )}

      {!isLoading && workflowFilesAndDirs && (
        <div className="mt-2 flex flex-col">
          {workflowFilesAndDirs?.files?.map((file: any) => (
            <div className="mt-1 flex cursor-pointer items-center text-sm">
              <FileText className="mr-2 h-4 w-4" />
              {file}
            </div>
          ))}
        </div>
      )}

      <div className="mt-4 flex justify-end">
        <Button
          variant="outline"
          size="sm"
          className="mr-1"
          disabled={createDir.isPending}
          onClick={async () => {
            const dirNamePrompt = prompt('Enter the name of the directory');
            if (!dirNamePrompt) return;
            await createDir.mutateAsync({
              dirName: dirNamePrompt,
              fullPath: `${basePath}${searchPath}`,
              orgId: org?.id || '',
              workflowId: workFlowId,
            });
          }}
        >
          Create dir
        </Button>

        <div>
          <input
            type="file"
            className={cn(
              'hidden', // Hide the default file input
            )}
            ref={fileInputRef}
            onChange={handleFileChange}
          />
          <Button
            variant="outline"
            size="sm"
            disabled={uploadFile.isPending}
            onClick={handleButtonClick}
          >
            <FileUp className="mr-1 h-3 w-3" /> Upload file
          </Button>
        </div>
      </div>
    </div>
  );
}
